import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid-pro-6";
import { TFunction } from "i18next";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import ColumnCell from "../ui/column-cell";
import ColumnEmployeeCell from "../ui/column-employee-cell";
import ColumnHeader from "../ui/column-header";

export interface JobsMatrixColumnConfig {
    field: string;
    headerName: string;
    checked: boolean;
    order: number;
}

// Define all available columns
const ALL_COLUMN_DEFINITIONS = (
    t: TFunction,
    searchText = ""
): GridColDef[] => [
    {
        field: "name",
        headerName: t("jobs-matrix_worker-name"),
        width: 200,
        minWidth: 150,
        headerAlign: "left",
        valueGetter: (params: GridValueGetterParams) =>
            `${params.row.firstName} ${params.row.lastName}`,
        renderHeader: () => <ColumnHeader text={t("jobs-matrix_worker-name")} />,
        renderCell: (params) => (
            <ColumnEmployeeCell params={params} searchText={searchText} />
        ),
    },
    {
        field: "companyEmployeeId",
        headerName: t("jobs-matrix_worker-id"),
        width: 200,
        minWidth: 120,
        headerAlign: "left",
        valueGetter: (params: GridValueGetterParams) => params.row.companyEmployeeId,
        renderHeader: () => <ColumnHeader text={t("jobs-matrix_worker-id")} />,
        renderCell: (params) => (
            <ColumnCell
                text={params.row.companyEmployeeId ?? "-"}
                searchText={searchText}
            />
        ),
        sortComparator: (v1: any, v2: any) => {
            if (v1 === v2) return 0;
            if (!v1) return 1;
            if (!v2) return -1;
            const num1 = /^\d+$/.test(String(v1).trim());
            const num2 = /^\d+$/.test(String(v2).trim());
            if (num1 && num2)
                return parseInt(String(v1), 10) - parseInt(String(v2), 10);
            if (num1) return -1;
            if (num2) return 1;
            return String(v1)
                .trim()
                .localeCompare(String(v2).trim(), undefined, { sensitivity: "base" });
        },
    },
    {
        field: "jobTitle",
        headerName: t("jobs-matrix_job-title"),
        width: 200,
        minWidth: 150,
        headerAlign: "left",
        valueGetter: (params: GridValueGetterParams) =>
            params.row.jobTitle ? params.row.jobTitle.trim().toLowerCase() : "",
        renderHeader: () => <ColumnHeader text={t("jobs-matrix_job-title")} />,
        renderCell: (params) => (
            <ColumnCell
                text={params.row.jobTitle ?? "-"}
                searchText={searchText}
            />
        ),
    },
    {
        field: "locationName",
        headerName: t("jobs-matrix_location"),
        width: 200,
        minWidth: 130,
        headerAlign: "left",
        valueGetter: (params: GridValueGetterParams) => params.row.locationName || "",
        renderHeader: () => <ColumnHeader text={t("jobs-matrix_location")} />,
        renderCell: (params) => (
            <ColumnCell text={params.row.locationName || "-"} searchText={searchText} />
        ),
    },
    {
        field: "workcenterName",
        headerName: t("jobs-matrix_workcenter"),
        width: 200,
        minWidth: 140,
        headerAlign: "left",
        valueGetter: (params: GridValueGetterParams) => params.row.workcenterName || "",
        renderHeader: () => <ColumnHeader text={t("jobs-matrix_workcenter")} />,
        renderCell: (params) => (
            <ColumnCell text={params.row.workcenterName || "-"} searchText={searchText} />
        ),
    },
    {
        field: "workerTypeName",
        headerName: t("jobs-matrix_worker-type"),
        width: 200,
        minWidth: 140,
        headerAlign: "left",
        valueGetter: (params: GridValueGetterParams) => params.row.workerTypeName || "",
        renderHeader: () => <ColumnHeader text={t("jobs-matrix_worker-type")} />,
        renderCell: (params) => (
            <ColumnCell text={params.row.workerTypeName || "-"} searchText={searchText} />
        ),
    },
    {
        field: "managerName",
        headerName: t("jobs-matrix_manager"),
        width: 200,
        minWidth: 130,
        headerAlign: "left",
        valueGetter: (params: GridValueGetterParams) => params.row.managerName || "",
        renderHeader: () => <ColumnHeader text={t("jobs-matrix_manager")} />,
        renderCell: (params) => (
            <ColumnCell text={params.row.managerName || "-"} searchText={searchText} />
        ),
    },
    {
        field: "primaryJobName",
        headerName: t("jobs-matrix_primary-job-role"),
        width: 200,
        minWidth: 160,
        headerAlign: "left",
        valueGetter: (params: GridValueGetterParams) => params.row.primaryJobName || "",
        renderHeader: () => <ColumnHeader text={t("jobs-matrix_primary-job-role")} />,
        renderCell: (params) => (
            <ColumnCell text={params.row.primaryJobName || "-"} searchText={searchText} />
        ),
    },
];

// Default columns that are always visible
const DEFAULT_COLUMNS = ["name"];

// Initial visible columns
const INITIAL_VISIBLE_COLUMNS = ["name"];

const useJobsMatrixColumns = (searchText = "") => {
    const { t } = useTranslation();
    const [selectedColumns, setSelectedColumns] = useState<string[]>(INITIAL_VISIBLE_COLUMNS);

    // Get all column definitions
    const allColumns = useMemo(() => ALL_COLUMN_DEFINITIONS(t, searchText), [t, searchText]);

    // Generate picker columns for the column selector
    const pickerColumns: JobsMatrixColumnConfig[] = useMemo(() => {
        return allColumns.map((col, index) => ({
            field: col.field,
            headerName: col.headerName || col.field,
            checked: selectedColumns.includes(col.field),
            order: index,
        }));
    }, [allColumns, selectedColumns]);

    // Get visible columns based on selection
    const visibleColumns = useMemo(() => {
        return allColumns.filter((col) => selectedColumns.includes(col.field));
    }, [allColumns, selectedColumns]);

    // Handle column selection changes
    const handleSelectColumns = (checked: boolean, field: string) => {
        setSelectedColumns((prev) => {
            if (checked && !prev.includes(field)) {
                return [...prev, field];
            } else if (!checked && prev.includes(field)) {
                return prev.filter((col) => col !== field);
            }
            return prev;
        });
    };

    // Get disabled columns (columns that cannot be unchecked)
    const disabledColumns = DEFAULT_COLUMNS;

    return {
        columns: visibleColumns,
        selectedColumns,
        pickerColumns,
        handleSelectColumns,
        disabledColumns,
    };
};

export default useJobsMatrixColumns;
